import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, X, Clock, MessageSquare, Users } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { timeAgo } from '../utils/timeAgo';

const getInitials = (name) =>
  name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'U';

const PersonRow = ({ person, children, onClick }) => (
  <Card className="p-4 bg-white border border-slate-100 shadow-sm flex items-center gap-4 hover:shadow-md transition-all">
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-4 flex-1 min-w-0 text-left"
    >
      <Avatar className="h-12 w-12">
        <AvatarImage src={person.profile_photo} />
        <AvatarFallback className="bg-[#1E3A8A] text-white">
          {getInitials(person.name)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 truncate">{person.name}</p>
        <p className="text-xs text-slate-500 truncate">
          {person.current_role || 'GBA Alumni'}
          {person.batch ? ` • ${person.batch}` : ''}
          {person.city ? ` • ${person.city}` : ''}
        </p>
      </div>
    </button>
    <div className="flex items-center gap-2 shrink-0">{children}</div>
  </Card>
);

const Network = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [sent, setSent] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, s, c] = await Promise.all([
        api.get('/connections/pending'),
        api.get('/connections/sent'),
        api.get('/connections'),
      ]);
      setPending(p.data);
      setSent(s.data);
      setConnections(c.data);
    } catch (err) {
      toast.error('Failed to load network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleAccept = async (id) => {
    try {
      await api.post(`/connections/${id}/accept`);
      toast.success('Connection accepted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to accept');
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Decline this connection request?')) return;
    try {
      await api.post(`/connections/${id}/reject`);
      toast.success('Request removed');
      fetchAll();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this pending request?')) return;
    try {
      await api.post(`/connections/${id}/reject`);
      toast.success('Request cancelled');
      fetchAll();
    } catch (err) {
      toast.error('Failed to cancel');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="network-page">
            <div className="mb-6 flex items-center gap-3">
              <Users className="h-8 w-8 text-[#1E3A8A]" />
              <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight">
                My Network
              </h1>
            </div>

            <Tabs defaultValue="requests" className="space-y-4">
              <TabsList className="bg-white">
                <TabsTrigger value="requests" data-testid="tab-network-requests">
                  Requests
                  {pending.length > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center rounded-full bg-[#22C55E] text-white text-xs px-2 py-0.5">
                      {pending.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="connections" data-testid="tab-network-connections">
                  Connections ({connections.length})
                </TabsTrigger>
                <TabsTrigger value="sent" data-testid="tab-network-sent">
                  Sent ({sent.length})
                </TabsTrigger>
              </TabsList>

              {/* Incoming requests */}
              <TabsContent value="requests" data-testid="incoming-requests">
                {loading ? (
                  <p className="text-slate-500 py-6 text-center">Loading...</p>
                ) : pending.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <p className="text-slate-500">No pending connection requests</p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {pending.map((req) => (
                      <PersonRow
                        key={req.connection_id}
                        person={req.requester}
                        onClick={() => navigate(`/profile/${req.requester.id}`)}
                      >
                        <span className="text-xs text-slate-500 hidden sm:inline">
                          {timeAgo(req.created_at)}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => handleAccept(req.connection_id)}
                          className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                          data-testid="accept-request-button"
                        >
                          <Check className="h-4 w-4 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(req.connection_id)}
                          className="text-red-600 hover:bg-red-50 border-red-200"
                          data-testid="reject-request-button"
                        >
                          <X className="h-4 w-4 mr-1" /> Decline
                        </Button>
                      </PersonRow>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Accepted connections */}
              <TabsContent value="connections" data-testid="accepted-connections">
                {loading ? (
                  <p className="text-slate-500 py-6 text-center">Loading...</p>
                ) : connections.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <p className="text-slate-500 mb-2">No connections yet</p>
                    <Button
                      onClick={() => navigate('/directory')}
                      className="bg-[#1E3A8A] hover:bg-[#172554] text-white"
                    >
                      Find Members
                    </Button>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {connections.map((c) => (
                      <PersonRow
                        key={c.connection_id}
                        person={c.partner}
                        onClick={() => navigate(`/profile/${c.partner.id}`)}
                      >
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate('/messages', { state: { partnerId: c.partner.id } })
                          }
                          className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
                          data-testid="message-connection-button"
                        >
                          <MessageSquare className="h-4 w-4 mr-1" /> Message
                        </Button>
                      </PersonRow>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Sent / outgoing pending */}
              <TabsContent value="sent" data-testid="outgoing-requests">
                {loading ? (
                  <p className="text-slate-500 py-6 text-center">Loading...</p>
                ) : sent.length === 0 ? (
                  <Card className="p-12 text-center bg-white">
                    <p className="text-slate-500">No outgoing requests</p>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {sent.map((req) => (
                      <PersonRow
                        key={req.connection_id}
                        person={req.target}
                        onClick={() => navigate(`/profile/${req.target.id}`)}
                      >
                        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {timeAgo(req.created_at)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancel(req.connection_id)}
                          className="text-slate-600"
                          data-testid="cancel-request-button"
                        >
                          Cancel
                        </Button>
                      </PersonRow>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Network;
